import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../services/api';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import MetricsCard from '../../components/Shared/MetricsCard';
import DataTable from '../../components/Shared/DataTable';
import { TopicTrendsChart, StrugglingStudentsWidget, AIInsightsSummaryWidget } from '../../components/Dashboard/AnalyticsCharts';
import ClassSelector from '../../components/ClassSelector';
import useClassContext from '../../hooks/useClassContext';
import { teacherNavItems } from '../../constants/navigation';

/**
 * TeacherDashboard - Main dashboard for teachers
 * Shows class overview, student list, and analytics
 * Allows creating student activation codes
 */
const TeacherDashboard = () => {
  const { currentUser } = useAuth();
  const { selectedClassId, setSelectedClassId, appendClassFilter } = useClassContext();
  const [dashboardData, setDashboardData] = useState(null);
  const [students, setStudents] = useState([]);
  const [classSections, setClassSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateCodeModal, setShowCreateCodeModal] = useState(false);
  const [createMode, setCreateMode] = useState('quick'); // 'quick' or 'detailed'
  const [codeCount, setCodeCount] = useState(1);
  const [studentForms, setStudentForms] = useState([]);
  const [createdCodes, setCreatedCodes] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const url = appendClassFilter('/api/teacher/dashboard');
        const data = await apiRequest(url, 'GET');
        setDashboardData(data);
        setStudents(data.students || []);
        if (data.class_sections) {
          setClassSections(data.class_sections);
        }
      } catch (err) {
        console.error('Failed to load dashboard:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [selectedClassId, appendClassFilter]);

  // Handle mode switch between quick and detailed
  const handleModeSwitch = (mode) => {
    setCreateMode(mode);
    if (mode === 'detailed' && studentForms.length === 0) {
      // Initialize with one empty form
      setStudentForms([{ first_name: '', last_name: '', display_name: '' }]);
    }
  };

  // Add a new student form
  const addStudentForm = () => {
    setStudentForms([...studentForms, { first_name: '', last_name: '', display_name: '' }]);
  };

  // Remove a student form
  const removeStudentForm = (index) => {
    setStudentForms(studentForms.filter((_, i) => i !== index));
  };

  // Update student form field
  const updateStudentForm = (index, field, value) => {
    const updated = [...studentForms];
    updated[index] = { ...updated[index], [field]: value };
    setStudentForms(updated);
  };

  // Create student activation codes
  const handleCreateCodes = async () => {
    setCreateLoading(true);
    try {
      let studentsData;

      if (createMode === 'quick') {
        // Generate placeholder students array
        const count = parseInt(codeCount);
        studentsData = Array.from({ length: count }, (_, i) => ({
          first_name: 'Student',
          last_name: `${i + 1}`,
          display_name: `Student ${i + 1}`,
          grade_level: currentUser?.grade_level || 'elementary'
        }));
      } else {
        // Use detailed student forms
        studentsData = studentForms.map((form, i) => ({
          first_name: form.first_name || 'Student',
          last_name: form.last_name || `${i + 1}`,
          display_name: form.display_name || `${form.first_name || 'Student'} ${form.last_name || i + 1}`,
          grade_level: currentUser?.grade_level || 'elementary'
        }));
      }

      const response = await apiRequest('/api/teacher/codes/student/create-bulk', 'POST', {
        students: studentsData
      });
      setCreatedCodes(response.students || []);

      // Refresh dashboard data
      const dashData = await apiRequest('/api/teacher/dashboard', 'GET');
      setDashboardData(dashData);
      setStudents(dashData.students || []);
    } catch (err) {
      alert(`Failed to create codes: ${err.message}`);
    } finally {
      setCreateLoading(false);
    }
  };

  // Reset modal
  const resetModal = () => {
    setShowCreateCodeModal(false);
    setCreatedCodes([]);
    setCreateMode('quick');
    setCodeCount(1);
    setStudentForms([]);
  };

  // Copy codes to clipboard
  const copyToClipboard = () => {
    const codesText = createdCodes.map(c => c.activation_code).join('\n');
    navigator.clipboard.writeText(codesText);
    alert('Codes copied to clipboard!');
  };

  // Student table columns
  const studentColumns = [
    {
      key: 'username',
      label: 'Student',
      sortable: true,
      render: (student) => (
        <div>
          <p className="font-medium text-gray-900">
            {student.first_name} {student.last_name}
          </p>
          <p className="text-xs text-gray-500">@{student.username}</p>
        </div>
      )
    },
    {
      key: 'grade_level',
      label: 'Grade',
      sortable: true,
      render: (student) => (
        <span className="capitalize">{student.grade_level || 'N/A'}</span>
      )
    },
    {
      key: 'sessions_count',
      label: 'Sessions',
      sortable: true,
      render: (student) => (
        <span className="font-medium">{student.sessions_count || 0}</span>
      )
    },
    {
      key: 'engagement_score',
      label: 'Engagement',
      sortable: true,
      render: (student) => {
        const score = student.engagement_score || 0;
        const colorClass = score >= 80 ? 'text-green-600' : score >= 60 ? 'text-yellow-600' : 'text-red-600';
        return (
          <span className={`font-semibold ${colorClass}`}>
            {score.toFixed(1)}%
          </span>
        );
      }
    },
    {
      key: 'last_active',
      label: 'Last Active',
      sortable: true,
      render: (student) => {
        if (!student.last_active) return <span className="text-gray-400">Never</span>;
        const date = new Date(student.last_active);
        const daysAgo = Math.floor((new Date() - date) / (1000 * 60 * 60 * 24));
        if (daysAgo === 0) return <span className="text-green-600">Today</span>;
        if (daysAgo === 1) return <span className="text-gray-600">Yesterday</span>;
        return <span className="text-gray-600">{daysAgo} days ago</span>;
      }
    },
    {
      key: 'activation_code',
      label: 'Activation Code',
      render: (student) => {
        if (student.activation_code_used) {
          return <span className="text-green-600 text-xs">✓ Activated</span>;
        }
        if (student.activation_code) {
          return (
            <button
              onClick={() => {
                navigator.clipboard.writeText(student.activation_code);
                alert(`Code copied: ${student.activation_code}`);
              }}
              className="text-indigo-600 hover:text-indigo-800 font-medium text-xs flex items-center"
              title={student.activation_code}
            >
              📋 Copy Code
            </button>
          );
        }
        return <span className="text-gray-400 text-xs">N/A</span>;
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (student) => {
        if (!student.is_active) {
          return <span className="text-gray-500">Inactive</span>;
        }
        if (student.activation_code_used) {
          return <span className="text-green-600 font-medium">Active</span>;
        }
        return <span className="text-yellow-600">Pending Activation</span>;
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (student) => (
        <button
          onClick={() => window.location.href = `/teacher/student/${student.id || student.student_id}`}
          className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
        >
          View Details →
        </button>
      )
    }
  ];

  if (loading) {
    return (
      <DashboardLayout
        title="Teacher Dashboard"
        navItems={teacherNavItems}
        userRole={currentUser?.user_type || 'teacher'}
        userName={currentUser?.full_name || 'Teacher'}
      >
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading dashboard...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout
        title="Teacher Dashboard"
        navItems={teacherNavItems}
        userRole={currentUser?.user_type || 'teacher'}
        userName={currentUser?.full_name || 'Teacher'}
      >
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading dashboard: {error}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Teacher Dashboard"
      navItems={teacherNavItems}
      userRole={currentUser?.user_type || 'teacher'}
      userName={currentUser?.full_name || 'Teacher'}
    >
      {/* Welcome Section */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900">
          Welcome back, {dashboardData?.teacher?.full_name || currentUser?.full_name}!
        </h2>
        <p className="text-gray-600 mt-1">
          {dashboardData?.teacher?.subject || 'Your Class'}
        </p>
      </div>

      {/* Class Section Selector */}
      {classSections.length > 0 && (
        <ClassSelector
          classSections={classSections}
          selectedClassId={selectedClassId}
          onClassChange={setSelectedClassId}
        />
      )}

      {/* Metrics Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <MetricsCard
          title="Total Students"
          value={dashboardData?.metrics?.total_students || 0}
          subtitle="in your class"
          icon="👥"
        />
        <MetricsCard
          title="Active Students"
          value={dashboardData?.metrics?.active_students || 0}
          subtitle="last 7 days"
          trend={`${Math.round(((dashboardData?.metrics?.active_students || 0) / (dashboardData?.metrics?.total_students || 1)) * 100)}%`}
          trendColor="green"
          icon="✅"
        />
        <MetricsCard
          title="Total Sessions"
          value={dashboardData?.metrics?.total_sessions || 0}
          subtitle="all time"
          icon="💬"
        />
        <MetricsCard
          title="Avg Engagement"
          value={`${(dashboardData?.metrics?.engagement_rate || 0).toFixed(1)}%`}
          subtitle="class average"
          trend={dashboardData?.metrics?.engagement_rate >= 75 ? '▲ Good' : '▼ Needs attention'}
          trendColor={dashboardData?.metrics?.engagement_rate >= 75 ? 'green' : 'yellow'}
          icon="📊"
        />
        <MetricsCard
          title="Engagement Score"
          value={`${(dashboardData?.metrics?.average_engagement_score || 0).toFixed(1)}`}
          subtitle="weighted average"
          trend={dashboardData?.metrics?.average_engagement_score >= 75 ? '▲ Great' : '▼ Monitor'}
          trendColor={dashboardData?.metrics?.average_engagement_score >= 75 ? 'green' : 'orange'}
          icon="⭐"
        />
      </div>

      {/* AI Insights and Alerts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Struggling Students Widget */}
        <StrugglingStudentsWidget strugglingStudents={dashboardData?.struggling_students || []} />

        {/* AI Insights Summary Widget */}
        {dashboardData?.ai_insights_summary && (
          <AIInsightsSummaryWidget insightsSummary={dashboardData.ai_insights_summary} />
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setShowCreateCodeModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
          >
            + Create Student Codes
          </button>
          <button
            onClick={() => window.location.href = '/teacher/analytics'}
            className="px-4 py-2 bg-white text-indigo-600 border-2 border-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors font-medium"
          >
            📈 View Class Analytics
          </button>
          <button
            onClick={() => window.location.href = '/teacher/insights'}
            className="px-4 py-2 bg-white text-indigo-600 border-2 border-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors font-medium"
          >
            🤖 View AI Insights
          </button>
        </div>
      </div>

      {/* Student List */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-900">My Students</h3>
          <span className="text-sm text-gray-600">
            {students.length} student{students.length !== 1 ? 's' : ''}
          </span>
        </div>
        <DataTable
          columns={studentColumns}
          data={students}
          emptyMessage="No students yet. Create activation codes to invite students!"
        />
      </div>

      {/* Top Topics (Class Trends) - Now using visualization */}
      {dashboardData?.most_asked_topics && dashboardData.most_asked_topics.length > 0 && (
        <TopicTrendsChart topics={dashboardData.most_asked_topics} />
      )}

      {/* Create Codes Modal */}
      {showCreateCodeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Create Student Activation Codes
            </h3>

            {createdCodes.length === 0 ? (
              <>
                {/* Mode Selection */}
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => handleModeSwitch('quick')}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                      createMode === 'quick'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Quick Create
                  </button>
                  <button
                    onClick={() => handleModeSwitch('detailed')}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                      createMode === 'detailed'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Add Student Names
                  </button>
                </div>

                {/* Quick Mode */}
                {createMode === 'quick' && (
                  <>
                    <p className="text-gray-600 mb-4">
                      How many student codes do you need?
                    </p>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={codeCount}
                      onChange={(e) => setCodeCount(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:border-indigo-500"
                    />
                    <p className="text-xs text-gray-500 mb-4">
                      Students will be named "Student 1", "Student 2", etc. You can update names later.
                    </p>
                  </>
                )}

                {/* Detailed Mode */}
                {createMode === 'detailed' && (
                  <>
                    <p className="text-gray-600 mb-4">
                      Enter student information (all fields optional):
                    </p>
                    <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
                      {studentForms.map((form, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-semibold text-gray-700">Student {index + 1}</span>
                            {studentForms.length > 1 && (
                              <button
                                onClick={() => removeStudentForm(index)}
                                className="text-red-600 hover:text-red-700 text-sm"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              placeholder="First Name (optional)"
                              value={form.first_name}
                              onChange={(e) => updateStudentForm(index, 'first_name', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-indigo-500"
                            />
                            <input
                              type="text"
                              placeholder="Last Name (optional)"
                              value={form.last_name}
                              onChange={(e) => updateStudentForm(index, 'last_name', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <input
                            type="text"
                            placeholder="Display Name (optional)"
                            value={form.display_name}
                            onChange={(e) => updateStudentForm(index, 'display_name', e.target.value)}
                            className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-indigo-500"
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={addStudentForm}
                      className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 mb-4 font-medium"
                    >
                      + Add Another Student
                    </button>
                  </>
                )}

                <div className="flex space-x-3">
                  <button
                    onClick={handleCreateCodes}
                    disabled={createLoading}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {createLoading ? 'Creating...' : 'Create Codes'}
                  </button>
                  <button
                    onClick={resetModal}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-green-600 font-semibold mb-3">
                  ✓ {createdCodes.length} code{createdCodes.length !== 1 ? 's' : ''} created successfully!
                </p>
                <div className="bg-gray-50 rounded p-3 mb-4 max-h-64 overflow-y-auto">
                  {createdCodes.map((codeObj, index) => (
                    <div key={index} className="font-mono text-sm text-gray-900 py-1">
                      {codeObj.activation_code}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  {createdCodes[0]?.expires_at
                    ? `Expires: ${new Date(createdCodes[0].expires_at).toLocaleDateString()}`
                    : 'Valid for 30 days'}
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={copyToClipboard}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    📋 Copy All Codes
                  </button>
                  <button
                    onClick={resetModal}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default TeacherDashboard;
